import { css, CSSObject, useTheme } from "@emotion/react";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MdCheck, MdClose } from "react-icons/md";

import { DialogPlacement } from "./Dialog";

type Props = {
  children?: React.ReactNode;
  onClose?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  hideHeader?: boolean;
  background?: string;
  contentCss?: CSSObject;
  placement?: DialogPlacement;
  className?: string;
  checkCloseButton?: boolean;
  outsideClickIgnoreRefs?: React.RefObject<HTMLElement | null>[];
  zIndex?: number;
};

const isIgnoredOutsideClick = (
  target: Node,
  contentElement: HTMLElement | null,
  ignoreRefs: React.RefObject<HTMLElement | null>[] | undefined,
): boolean => {
  if (contentElement?.contains(target)) return true;
  return ignoreRefs?.some((ref) => ref.current?.contains(target)) ?? false;
};

export const DialogPopover: React.FC<Props> = (props) => {
  const theme = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const layerZIndex = props.zIndex ?? 1000;

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (isIgnoredOutsideClick(target, contentRef.current, props.outsideClickIgnoreRefs)) return;
      props.onClose?.();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.onClose?.();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [props.onClose, props.outsideClickIgnoreRefs]);

  return createPortal(
    <div
      ref={contentRef}
      css={css(
        {
          position: "fixed",
          zIndex: layerZIndex,
          margin: "2rem",
          border: "none",
          outline: "none",
          borderRadius: theme.largeBorderRadius,
          maxWidth: theme.siteMaxWidth,
          background: props.background ?? theme.colors.background,
          maxHeight: "calc(100% - 4rem)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.25)",
        },
        props.contentCss,
      )}
      className={props.className}
    >
      {!props.hideHeader && (
        <div
          css={{
            padding: "0.75rem 1rem",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
            background: theme.colors.primary,
          }}
        >
          <div
            css={{
              fontFamily: theme.displayFontFamily,
              margin: 0,
              fontSize: "1rem",
              fontWeight: "normal",
              color: "white",
              width: "100%",
            }}
          >
            {props.header}
          </div>
          <button
            onClick={props.onClose}
            css={{
              background: "none",
              border: "none",
              padding: "0.25rem",
              margin: "-0.25rem",
              cursor: "pointer",
              borderRadius: theme.smallBorderRadius,
              color: "white",
              display: "flex",
              alignItems: "center",
            }}
          >
            {props.checkCloseButton ? <MdCheck size="1.5rem" /> : <MdClose size="1.5rem" />}
          </button>
        </div>
      )}
      <div css={{ minHeight: 0, padding: "1rem", flex: 1, display: "flex", flexDirection: "column" }}>
        {props.children}
      </div>
      {props.footer && (
        <div css={{ padding: "0.75rem 1rem", marginTop: "auto" }}>
          <div css={{ display: "flex", flexDirection: "row" }}>{props.footer}</div>
        </div>
      )}
    </div>,
    document.body,
  );
};
